import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, RefreshCw, Send, LogOut, AlertCircle, CheckCircle } from 'lucide-react';
import { auth } from '../services/firebase';
import { sendEmailVerification, signOut } from 'firebase/auth';
import { apiService } from '../services/apiService';
import Logo from '../components/Logo';
import { usePortfolio } from '../context/PortfolioContext';

import { useSEO } from '../hooks/useSEO';

export const VerifyEmailPage: React.FC = () => {
  useSEO({
    title: 'Verify Email',
    description: 'Verifikasi alamat email Anda untuk mengaktifkan akun CatatCrypto dan mulai melacak transaksi Anda.',
    keywords: 'verify email, email verification, activate account, catatcrypto activation'
  });

  const navigate = useNavigate();
  const { clearSessionCache } = usePortfolio();
  const [userEmail, setUserEmail] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Monitor auth state
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUserEmail(user.email || '');
        if (user.emailVerified) {
          // If already verified on Firebase, try upgrading backend session
          handleVerifiedSession(user);
        }
      } else {
        // No user logged in Firebase, redirect to register
        navigate('/register');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleVerifiedSession = async (firebaseUser: any) => {
    try {
      const idToken = await firebaseUser.getIdToken(true);
      const data = await apiService.loginWithFirebase(idToken);
      
      clearSessionCache();
      localStorage.setItem('has_cex_accounts', data.has_cex_accounts ? 'true' : 'false');
      localStorage.setItem('auth_token', data.token);

      // Restore user_form_data with verified backend user details so onboarding knows the userId
      const defaultFormData = {
        userId: data.user.id,
        name: data.user.name,
        email: data.user.email,
        password: '',
        traderType: '',
        gender: '',
        birthYear: '',
        goal: '',
        sources: [],
        plan: 'free',
        broker: '',
        method: 'auto',
        apiKey: '',
        apiSecret: '',
        apiPassphrase: '',
        trade: { symbol: '', direction: 'Long', date: '2026-04-29', time: '14:30:00', quantity: '', price: '', commissions: '', fees: '' }
      };
      localStorage.setItem('user_form_data', JSON.stringify(defaultFormData));

      if (data.email_verified) {
        const hasProfile = data.profile && Object.keys(data.profile).length > 0;
        if (data.has_cex_accounts || hasProfile) {
          window.location.href = '/dashboard';
        } else {
          localStorage.setItem('app_step', '2');
          window.location.href = '/onboarding';
        }
      }
    } catch (err: any) {
      console.error('[VerifyEmail] Backend sync failed:', err);
    }
  };

  const handleCheckStatus = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    setError('');
    setMessage('');

    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        await currentUser.reload();
        if (currentUser.emailVerified) {
          setSuccess(true);
          setMessage('Email successfully verified! Redirecting you...');
          setTimeout(() => {
            handleVerifiedSession(currentUser);
          }, 1500);
        } else {
          setError('Your email has not been verified yet. Please check your inbox or spam folder.');
        }
      } else {
        navigate('/register');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to refresh verification status.');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleResendLink = async () => {
    if (isSending) return;
    setIsSending(true);
    setError('');
    setMessage('');

    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        await sendEmailVerification(currentUser);
        setMessage(`A new verification link has been sent to ${currentUser.email}.`);
      } else {
        setError('Session expired. Please log in again.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to resend verification link.');
    } finally {
      setIsSending(false);
    }
  };

  const handleLogout = async () => {
    try {
      clearSessionCache();
      await signOut(auth);
      window.location.href = '/register';
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col justify-center items-center p-6 font-sans select-none relative overflow-hidden">
      {/* Background glow effects */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-red-950/20 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-neutral-900/40 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-md w-full bg-[#0e0f17] border border-neutral-900 rounded-3xl p-8 text-center relative z-10 shadow-2xl">
        <Logo size="lg" centered />

        <div className="space-y-6 mt-8">
          <div className="w-16 h-16 bg-red-500/10 border border-red-500/30 rounded-2xl flex items-center justify-center mx-auto text-red-500 shadow-inner">
            <Mail size={32} />
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-black tracking-tight text-white">Verify Your Email</h1>
            <p className="text-xs text-neutral-400 leading-relaxed max-w-sm mx-auto">
              We have sent an email verification link to:
            </p>
            <p className="text-sm font-extrabold text-red-500 truncate max-w-xs mx-auto">
              {userEmail || 'Loading email...'}
            </p>
            <p className="text-[11px] text-neutral-500 leading-relaxed max-w-xs mx-auto pt-2">
              Please click the link in the email to verify your account and unlock all features of CatatCrypto.
            </p>
          </div>

          {/* Messages */}
          {error && (
            <div className="p-3 bg-red-900/10 border border-red-900/30 rounded-2xl text-red-500 text-xs font-bold flex items-center gap-2 justify-center">
              <AlertCircle size={14} />
              <span>{error}</span>
            </div>
          )}

          {message && (
            <div className="p-3 bg-green-900/10 border border-green-900/30 rounded-2xl text-green-500 text-xs font-bold flex items-center gap-2 justify-center">
              <CheckCircle size={14} />
              <span>{message}</span>
            </div>
          )}

          <div className="space-y-3 pt-4">
            <button
              onClick={handleCheckStatus}
              disabled={isRefreshing || success}
              className="w-full py-3.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-colors text-sm shadow-lg shadow-red-950/20"
            >
              <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : ''} />
              I Have Verified / Check Status
            </button>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleResendLink}
                disabled={isSending || success}
                className="py-3 bg-neutral-900 hover:bg-neutral-800 disabled:opacity-50 text-white border border-neutral-800 hover:border-neutral-700 font-bold rounded-xl flex items-center justify-center gap-2 transition-colors text-xs"
              >
                <Send size={12} />
                Resend Link
              </button>

              <button
                onClick={handleLogout}
                className="py-3 bg-neutral-900 hover:bg-red-900/20 hover:text-red-500 text-neutral-400 border border-neutral-800 hover:border-red-900/30 font-bold rounded-xl flex items-center justify-center gap-2 transition-colors text-xs"
              >
                <LogOut size={12} />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
